import { type CalendarRef, XCalendar } from '@adornis/calendar/functional-calendar.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { from, map, type Observable } from 'rxjs';
import {
  subscribeAllowedPartnerCompanies,
  subscribeScopedAvailabilities,
  subscribeScopedFieldEngineers,
} from '../../api/scoped-queries.js';
import type { SmartTeamsCompany, SmartTeamsFieldEngineer } from '../../db/index.js';
import { getFieldEngineerColor } from '../components/time-grid-utils.js';

interface CalendarEvent {
  id?: string;
  title: string;
  start: string;
  end: string;
  backgroundColor?: string;
  borderColor?: string;
  extendedProps?: Record<string, unknown>;
}

/**
 * Availability Calendar View - Shows FE availabilities using XCalendar
 */
export const AvailabilityCalendar = stateful(() => {
  const calendarRef = useMemo(() => [{} as CalendarRef], []);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);

  // Load companies (backend-scoped)
  const companiesQuery = useQuery(
    () => subscribeAllowedPartnerCompanies()({ _id: 1, name: 1, address: { zipCity: { city: 1 } } }),
    [],
  );

  // Load field engineers (backend-scoped, optionally filtered by company)
  const fesQuery = useQuery(
    () =>
      subscribeScopedFieldEngineers(selectedCompanyId ?? undefined)({
        _id: 1,
        name: 1,
        partnerCompanyIds: 1,
        defaultLocation: { city: 1 },
      }),
    [selectedCompanyId],
  );
  if (fesQuery.loading) return html` loading... `;

  const companies = (companiesQuery.value ?? []) as SmartTeamsCompany[];
  const allFieldEngineers = (fesQuery.value ?? []) as SmartTeamsFieldEngineer[];

  // Get unique cities from FEs
  const allCities = [
    ...new Set(allFieldEngineers.map(fe => fe.defaultLocation?.city).filter((city): city is string => !!city)),
  ].sort();

  // Apply city filter client-side
  const filteredFieldEngineers = selectedCity
    ? allFieldEngineers.filter(fe => fe.defaultLocation?.city === selectedCity)
    : allFieldEngineers;

  const feMap = new Map(allFieldEngineers.map(fe => [fe._id, fe]));

  // Events provider for XCalendar - fetches availabilities for visible date range
  // Note: Availabilities are already permission-scoped by the server
  const eventsProvider = (start: Date, end: Date): Observable<CalendarEvent[]> => {
    return from(
      subscribeScopedAvailabilities(
        undefined,
        start,
        end,
      )({ _id: 1, fieldEngineerId: 1, date: 1, startTime: 1, endTime: 1 }),
    ).pipe(
      map(availabilities => {
        if (!Array.isArray(availabilities)) return [];
        return availabilities
          .filter(av => av.date && av.fieldEngineerId)
          .map(av => {
            const fe = feMap.get(av.fieldEngineerId!);
            if (!fe) return undefined;
            const color = getFieldEngineerColor(av.fieldEngineerId!);
            const date = new Date(av.date!);
            // Use local date to avoid timezone shift
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

            return {
              id: av._id,
              title: fe?.name ?? 'Unknown',
              start: `${dateStr}T${av.startTime}:00`,
              end: `${dateStr}T${av.endTime}:00`,
              backgroundColor: color,
              borderColor: color,
              extendedProps: { fieldEngineerId: av.fieldEngineerId, fieldEngineerName: fe?.name },
            } as CalendarEvent;
          })
          .filter(Boolean) as CalendarEvent[];
      }),
    );
  };

  const isLoading = companiesQuery.loading || fesQuery.loading;

  return html`
    <div class="flex flex-col gap-4 p-4 h-full">
      <!-- Header with filters -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <h1 class="text-2xl font-bold">Availability Calendar</h1>
          ${isLoading ? html`<span class="loading loading-spinner loading-sm text-primary"></span>` : nothing}
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <!-- Company Filter -->
          <select
            class="select select-bordered select-sm"
            @change=${(e: Event) => {
              setSelectedCompanyId((e.target as HTMLSelectElement).value || null);
              // Refresh calendar when filter changes
              calendarRef.current?.refetchEvents();
            }}
          >
            <option value="">All Partner Companies</option>
            ${companies.map(
              c => html`
                <option value=${c._id} ?selected=${selectedCompanyId === c._id}>
                  ${c.name}${c.address?.zipCity?.city ? ` (${c.address.zipCity.city})` : ''}
                </option>
              `,
            )}
          </select>

          <!-- City Filter -->
          <select
            class="select select-bordered select-sm"
            @change=${(e: Event) => {
              setSelectedCity((e.target as HTMLSelectElement).value || null);
              calendarRef.current?.refetchEvents();
            }}
          >
            <option value="">All Cities</option>
            ${allCities.map(city => html` <option value=${city} ?selected=${selectedCity === city}>${city}</option> `)}
          </select>
        </div>
      </div>

      <!-- XCalendar -->
      <div class="flex-1 min-h-0">
        ${XCalendar({
          week: true,
          eventsProvider,
          calendarRef,
          height: '100%',
          class: 'h-full',
          options: {
            locale: 'en',
            buttonText: { today: 'today', month: 'month', week: 'week', day: 'day', list: 'list' },
            firstDay: 1, // Start week on Monday
            slotMinTime: '06:00:00',
            slotMaxTime: '22:00:00',
            nowIndicator: true,
            weekends: true,
            slotDuration: '00:30:00',
            eventDisplay: 'block',
          },
        })}
      </div>

      <!-- Legend / Stats -->
      <div class="flex items-center justify-between text-sm bg-base-200 rounded-lg p-3">
        <div class="flex items-center gap-4">
          <span class="text-base-content/70">
            ${filteredFieldEngineers.length} Field Engineers ${selectedCompanyId || selectedCity ? '(filtered)' : ''}
          </span>
        </div>
      </div>
    </div>
  `;
});
